import './HvacPage.scss';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useParametricSelector } from 'src/hooks/useParametricSelector';
import { BottomNavigationHelper } from 'src/modules/common/components/BottomNavigationHelper';
import { Breadcrumb } from 'src/modules/common/components/Breadcrumb';
import { BreadcrumbContainer } from 'src/modules/common/components/BreadcrumbContainer';
import { PageDescription } from 'src/modules/common/components/PageDescription';
import { sub, sup } from 'src/modules/common/constants/formattingHelpers';
import { AppLayout } from 'src/modules/common/layouts/AppLayout';
import { getEvaluationResults } from 'src/modules/evaluation/selectors/getEvaluationResults';
import { getHvacFormData } from 'src/modules/hvac/selectors/getHvacFormData';
import { getHvacData, getHvacResults } from 'src/modules/hvac/selectors/getHvacResults';
import { getHvacResultsIncompleteResults } from 'src/modules/hvac/selectors/getHvacResultsIncompleteResults';
import { getHvacResultsInsufficientInputs } from 'src/modules/hvac/selectors/getHvacResultsInsufficientInputs';
import { HvacResultSavingsView } from 'src/modules/hvac/views/HvacResultSavingsView';
import { getProjectDetails } from 'src/modules/project-view/selectors/getProjectDetails';
import { ShareProject } from 'src/modules/sharing-projects/containers/ShareProject';

export const HvacResultsPage = (): React.ReactElement => {
  const intl = useIntl();
  const project = useSelector(getProjectDetails);

  const data = useParametricSelector(getHvacData, project);
  const results = useParametricSelector(getHvacResults, project);
  const evaluationResults = useParametricSelector(getEvaluationResults, project);
  const insufficientInputs = useParametricSelector(getHvacResultsInsufficientInputs, project);
  const incompleteResults = useParametricSelector(getHvacResultsIncompleteResults, project);

  const formData = useParametricSelector(getHvacFormData, project);

  const heatingEnabled = true; // formData.assessment.areas[HvacArea.HEATING].enabled;
  const coolingEnabled = true; // formData.assessment.areas[HvacArea.COOLING].enabled;
  const ventilationEnabled = true; // formData.assessment.areas[HvacArea.VENTILATION].enabled;

  const tabsDisabled = !heatingEnabled && !coolingEnabled && !ventilationEnabled;
  const generationTabDisabled = !heatingEnabled && !coolingEnabled;

  const navigation = (
    <BottomNavigationHelper
      routeBackText={intl.formatMessage({ id: 'common/bottomNavigation/hvac/results/back' })}
      routeBackPath={`/project/${encodeURIComponent(project.id)}/hvac/generation`}
    />
  );

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage({ id: 'hvac/pageTitle' })}
        </title>
      </Helmet>

      <AppLayout>

        <div className="bp-hvac-page">
          <div className="bp-hvac-page__content">
            <BreadcrumbContainer>
              <Breadcrumb
                as={Link}
                to={`/project/${encodeURIComponent(project.id)}`}
                title={intl.formatMessage({ id: 'project/toolbar/navigation/basedata' })}
                value={<FormattedMessage id="basedata/tabs/value" values={{ sub, sup }}/>}
                isEmpty={false}
                isFilled={true}
                disabled={false}
              />
              <Breadcrumb
                as={Link}
                to={`/project/${encodeURIComponent(project.id)}/hvac/emission`}
                title={intl.formatMessage({ id: 'hvac/tabs/emission/title' })}
                value={<FormattedMessage id="hvac/tabs/emission/value" values={{ sub, sup }}/>}
                isEmpty={false}
                isFilled={true}
                disabled={tabsDisabled}
              />

              <Breadcrumb
                as={Link}
                to={`/project/${encodeURIComponent(project.id)}/hvac/distribution`}
                title={intl.formatMessage({ id: 'hvac/tabs/distribution/title' })}
                value={<FormattedMessage id="hvac/tabs/distribution/value" values={{ sub, sup }}/>}
                isEmpty={false}
                disabled={tabsDisabled}
                isFilled={true}
              />

              <Breadcrumb
                as={Link}
                to={`/project/${encodeURIComponent(project.id)}/hvac/generation`}
                title={intl.formatMessage({ id: 'hvac/tabs/generation/title' })}
                value={<FormattedMessage id="hvac/tabs/generation/value" values={{ sub, sup }}/>}
                isEmpty={generationTabDisabled}
                disabled={generationTabDisabled}
                isFilled={!generationTabDisabled}
              />

              <Breadcrumb
                as={Link}
                to={`/project/${encodeURIComponent(project.id)}/hvac/results`}
                title={intl.formatMessage({ id: 'hvac/tabs/results/title' })}
                value={<FormattedMessage id="hvac/tabs/results/value" values={{ sub, sup }}/>}
                isEmpty={false}
                disabled={tabsDisabled}
                isFilled={false}
              />
            </BreadcrumbContainer>

            {navigation}

            <PageDescription
              className="bp-hvac-page-description--container"
              title="hvac/results/header"
              description="hvac/results/description"
            />

            <HvacResultSavingsView
              form={formData}
              results={results}
              evaluationResults={evaluationResults}
              insufficientInputs={insufficientInputs}
              incompleteResults={incompleteResults}
            />

            {navigation}

          </div>
        </div>
      </AppLayout>

      <ShareProject/>
    </>
  );
};
